import { ComponentParameterEnhancerFunction } from '@uniformdev/canvas';

type ContentfulEntry = {
	sys: { id: string; type: string };
	fields: Record<string, unknown>;
};

type ContentfulResponse = {
	sys?: { type: string };
	items?: unknown[];
	includes?: { Entry?: ContentfulEntry[] };
	total?: number;
	limit?: number;
	skip?: number;
};

export type ContentfulEnvelope = {
	items: unknown[];
	total?: number;
	limit?: number;
	skip?: number;
};

export function isContentfulEnvelope(value: unknown): value is ContentfulEnvelope {
	return (
		typeof value === 'object' &&
		value !== null &&
		!Array.isArray(value) &&
		Array.isArray((value as ContentfulEnvelope).items)
	);
}

/**
 * Stitches Contentful `includes.Entry` back into each item's linked fields
 * before the gallery component sees them.
 *
 * Contentful's REST Delivery API never inlines resolved entries — linked fields
 * remain as `{ sys: { type: "Link", id: "..." } }` stubs in `items[].fields`,
 * while the resolved data lives in the `includes.Entry` sidecar. When the
 * Uniform data binding is set to `$` (the full response) instead of `$.items`,
 * this enhancer can stitch them inline so the gallery's filter engine can
 * traverse paths like `fields.category[].fields.urlSlug`.
 *
 * It also preserves pagination metadata (`total`, `limit`, `skip`) so the
 * gallery component can use them for future pagination support.
 */
export const contentfulIncludesEnhancer: ComponentParameterEnhancerFunction<unknown> = ({
	parameter,
}) => {
	const response = parameter.value as ContentfulResponse;

	if (Array.isArray(response)) {
		return { items: response };
	}

	const rawItems = response?.items ?? [];
	const includedEntries = response?.includes?.Entry;

	if (!Array.isArray(includedEntries) || includedEntries.length === 0) {
		return {
			items: rawItems,
			total: response?.total,
			limit: response?.limit,
			skip: response?.skip,
		};
	}

	const entryMap = new Map<string, ContentfulEntry>(
		includedEntries.map((entry) => [entry.sys.id, entry]),
	);

	const stitchedItems = rawItems.map((item) => {
		if (typeof item !== 'object' || item === null) return item;
		const typedItem = item as Record<string, unknown>;
		const fields = typedItem['fields'];
		if (typeof fields !== 'object' || fields === null) return item;

		const stitchedFields = stitchFields(
			fields as Record<string, unknown>,
			entryMap,
			new Set<string>(),
		);

		return { ...typedItem, fields: stitchedFields };
	});

	return {
		items: stitchedItems,
		total: response?.total,
		limit: response?.limit,
		skip: response?.skip,
	};
};

/**
 * Walks a `fields` object and replaces any Contentful Link stubs with their
 * resolved entries from the entryMap. Handles both single links and arrays.
 */
function stitchFields(
	fields: Record<string, unknown>,
	entryMap: Map<string, ContentfulEntry>,
	visited: Set<string>,
): Record<string, unknown> {
	return Object.fromEntries(
		Object.entries(fields).map(([key, value]) => [key, stitchValue(value, entryMap, visited)]),
	);
}

function stitchValue(
	value: unknown,
	entryMap: Map<string, ContentfulEntry>,
	visited: Set<string>,
): unknown {
	if (Array.isArray(value)) {
		return value.map((v) => stitchValue(v, entryMap, visited));
	}
	if (isLinkStub(value)) {
		const sys = (value as Record<string, Record<string, string>>)['sys'];
		const id = sys?.['id'];
		if (!id) return value;
		if (visited.has(id)) return value;
		const resolved = entryMap.get(id);
		if (!resolved) return value;
		return {
			...resolved,
			fields: stitchFields(resolved.fields, entryMap, new Set(visited).add(id)),
		};
	}
	return value;
}

function isLinkStub(value: unknown): boolean {
	if (typeof value !== 'object' || value === null) return false;
	const sys = (value as Record<string, unknown>)['sys'];
	if (typeof sys !== 'object' || sys === null) return false;
	return (sys as Record<string, unknown>)['type'] === 'Link';
}
